import React, { useLayoutEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import './PageHeaderBand.css';

/**
 * The page-header band — eyebrow, title, description, actions and a stat strip
 * in ONE bordered band, in the `--fi-*` re-skin namespace.
 *
 * THIS IS THE PAGE LAYOUT GOLD STANDARD'S HEADER. QuotesScreen and
 * RateSheetsScreen are the reference pages; CLAUDE.md describes the standard
 * from them. The stat-text clearance of the circle in PageHeaderBand.css was
 * measured on those two pages only — a page with longer stat copy or a
 * description line has to be re-measured before it adopts this band.
 *
 * IT PAINTS A PERMANENTLY-NAVY BAR. `--fi-brand` is the one colour in the
 * `--fi-*` palette that stays a near-black navy in both halves, so the ink on
 * it has to stay near-white in both — and for one release it could not, because
 * theme.css remaps the plain white text utility to the heading colour under
 * `:root[data-theme="light"]` with `!important` for 958 elements that need it.
 * A navy band built before that was answered read correctly in the dark
 * screenshot and lost its title in light mode, so the first release shipped in
 * a theme-following fill instead.
 *
 * The remap is not the thing to change — it is load-bearing, and flipping it
 * repaints those 958 sites. theme.css keeps a short list of fills that stay
 * dark in light mode and re-asserts near-white ink on them at equal specificity
 * later in the cascade; `--fi-brand`'s fill is now a member of that list, and
 * that is the whole mechanism holding this band's title up.
 *
 * Every piece of ink on it is therefore spelled in constant white opacities
 * rather than in the theme's ink tokens: those tokens follow the theme and
 * would put a dark steel eyebrow on navy the moment the app switched to light.
 *
 * THE STAT STRIP IS PART OF THAT HALF NOW, and it was not always. It shipped as
 * a row of theme-surface cards, which is why its ink could be the theme's ink
 * and need no override — and also why it read as white cards pasted onto the
 * navy rather than as part of the band. With the cards gone the stats sit on
 * `--fi-brand` itself, so the same constraint that governs the title governs
 * every figure, label and sub-line in the strip.
 *
 * Presentational only: it fetches nothing and formats nothing. `value` arrives
 * as an already-decided ReactNode, so the em-dash rule stays with the screen
 * that knows whether a figure was absent. See src/features/fieldiq-handoff.
 */

/**
 * The decorations inside the navy half, and why the patterned ones are
 * spelled as a style object instead of in utilities.
 *
 * Both have to stay near-white-on-navy in BOTH halves of the palette, for the
 * same reason the ink above does: `--fi-brand` is the one colour that does not
 * go pale when the app switches, so anything drawn on it has to ignore the
 * switch. Utilities cannot ignore it — theme.css remaps colour utilities under
 * `:root[data-theme="light"]`, which is correct for the surfaces that do go
 * pale and wrong for this one. A style object is unreachable by a stylesheet
 * selector, and that is the one situation where being unreachable is the
 * property you want rather than the bug it usually is.
 *
 * The lines are drawn as two one-pixel stripes repeated over a 28px tile —
 * graph paper, at 5% white. On `--fi-brand` that lifts the field from
 * rgb(16,26,45) to about rgb(28,37,56): legible as texture at arm's length,
 * and far too faint to compete with the eyebrow sitting on top of it.
 */
const BAND_WEAVE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),' +
    'linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
  backgroundSize: '28px 28px',
};

/*
 * The hero circle and its glow are NOT here. They are a pseudo-element and a
 * gradient layer on the band, which a style object cannot express, so they
 * live in PageHeaderBand.css with their source and measurements. This file
 * only names the band and the three blocks that must paint over the circle.
 */

/**
 * Stat accents, keyed on every member — no fallback branch that guesses.
 *
 * THE TONE NOW PAINTS THE ICON AND NOTHING ELSE, and the figure beside it is
 * white in every tone. Two things drove that.
 *
 * The strip used to be four theme-surface cards laid on the navy, which read as
 * white cards pasted onto the band rather than as part of it. With the cards
 * gone the stats sit on `--fi-brand` directly, and `--fi-brand` is the one
 * colour in the palette that stays a near-black navy in both halves — so
 * anything drawn here has to ignore the theme switch, exactly like the weave
 * below. The old tones could not: `text-fi-blue` is a pale sky in the dark half
 * and a #1d4ed8 in the light one, which is a figure nobody can read on navy the
 * moment the app is switched. Nothing would report it — the class is in the
 * DOM, the build is clean, the number is simply not there.
 *
 * The `--fi-signal-*` hues exist for that: declared once, no light twin, so the
 * switch cannot reach them. See src/styles/fieldiq-tokens.css.
 *
 * A figure in a hue was also the wrong emphasis for a strip with no card edges
 * around it. The figures are what the eye lands on and they are all equally
 * important; the hue belongs on the small mark that says which figure it is.
 */
export type StatTone = 'neutral' | 'info' | 'success' | 'accent';

const STAT_TONE: Record<StatTone, string> = {
  neutral: 'text-white/60',
  info: 'text-fi-signal-blue',
  success: 'text-fi-signal-green',
  accent: 'text-fi-signal-amber',
};

/**
 * Column counts as LITERAL class strings.
 *
 * Tailwind's scanner has no evaluator: a template built as `sm:grid-cols-${n}`
 * emits no rule at all and the strip silently collapses to one column. Every
 * count this component supports is spelled out here so the scanner can see it.
 */
const STAT_COLUMNS: Record<2 | 3 | 4, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

export interface PageHeaderStat {
  /** A lucide icon; it inherits the chip's colour, so pass no colour class. */
  icon: React.ReactNode;
  /** The figure itself, already formatted — '—' when the source was absent. */
  value: React.ReactNode;
  label: string;
  /** Small line under the label. Static copy, never a recomputed figure. */
  sub?: string;
  tone?: StatTone;
  /** Renders a spinner in place of the value while the query is in flight. */
  loading?: boolean;
}

export interface PageHeaderBandProps {
  eyebrow: string;
  title: string;
  /** Optional. One line; omitted entirely when absent, leaving no gap. */
  description?: string;
  /** Optional. Two to four; more than four is a rejected shape, not a wrap. */
  stats?: PageHeaderStat[];
  /** Optional. Right-aligned beside the title, stacked below it under lg. */
  actions?: React.ReactNode;
  /** Optional. Renders the plain arrow; no arrow at all when omitted. */
  onBack?: () => void;
  backLabel?: string;
}

/**
 * One stat, with NO surface of its own.
 *
 * There is deliberately no fill, no border and no corner radius here: the band's
 * navy — and the weave drawn over it — has to run straight through, which is
 * what makes the strip part of the band instead of four panels resting on it.
 * The separators are the parent's business, because a rule drawn per stat is a
 * rule the last one also draws, off the end of the strip.
 *
 * ITS INK IS SPELLED IN CONSTANT WHITE OPACITIES for the reason the header half
 * above it is: theme.css remaps colour utilities under the light attribute, and
 * the ink tokens follow the theme, so `text-fi-ink-muted` here would put dark
 * steel on navy in light mode. `text-white/60` is untouched by that remap —
 * only the exact `.text-white` selector is rewritten, and the band's own
 * `bg-fi-brand` exemption is what holds THAT one up for the figure below, which
 * carries no colour class at all and inherits it.
 */
function Stat({ icon, value, label, sub, tone = 'neutral', loading = false }: PageHeaderStat) {
  return (
    <div data-testid="page-header-stat" className="py-1 lg:px-5 lg:first:pl-0 lg:last:pr-0">
      <p className="flex items-center gap-1.5 text-[0.65rem] font-black uppercase tracking-[0.14em] text-white/60">
        <span className={`shrink-0 ${STAT_TONE[tone]}`}>{icon}</span>
        <span className="truncate">{label}</span>
      </p>
      {loading ? (
        <span
          role="status"
          aria-label={`${label} loading`}
          className="mt-1.5 block h-7 w-20 animate-pulse rounded-fi-control bg-white/10"
        />
      ) : (
        <p data-testid="page-header-figure" className="fi-band__figure mt-0.5 font-display font-extrabold tracking-tight">
          {/* The inline box is the ink: its width is what the fit measures. */}
          <span data-testid="page-header-figure-ink">{value}</span>
        </p>
      )}
      {sub && <p className="mt-0.5 truncate text-xs leading-5 text-white/55">{sub}</p>}
    </div>
  );
}

/**
 * Shrinks every figure in the strip by ONE shared factor until the widest of
 * them fits its column. Never grows past the full size, never wraps.
 *
 * WHY THIS EXISTS. The columns are fractions of the band, and a figure is one
 * unbreakable word. A seven-figure amount with its separators and cents is
 * about 7.8em of ink — 233px at full size — and a middle column at a 1024px
 * viewport is 134px wide, so the figure ran into its neighbour and, in the
 * fourth column, past the band's clipped edge. A clipped dollar figure reads as
 * a different amount, which is worse than any layout defect.
 *
 * WHY SHRINK, AND NOT THE OTHER TWO. Wrapping splits one number across two
 * lines, which reads as two numbers. Reflowing to fewer columns moves every
 * label and sub-line, and those positions are exactly what the hero circle's
 * offset in PageHeaderBand.css was measured against — a reflow would void that
 * measurement at every width it applied. Shrinking moves nothing: the figure's
 * line box stays its full-size height (see `.fi-band__figure`), so the label
 * above and the sub-line below sit where they were measured.
 *
 * WHY ONE FACTOR FOR THE STRIP, AND WHY MEASURED. A pure-CSS size keyed to the
 * column width would have to assume a character count, scale a two-digit
 * count as hard as a seven-figure amount, and still clip the first figure
 * longer than its budget. Measuring the ink shrinks only when something would
 * overflow, only by as much as it needs, and keeps the four figures one size —
 * a strip whose figures differ in size reads as figures that differ in weight.
 *
 * The factor is a custom property on the strip, read by the figure's rule in
 * the stylesheet. Ink is measured at the current factor and divided back out,
 * so a pass after scaling down computes the same factor and the observer settles.
 * No ResizeObserver (jsdom) or no layout (a zero width) leaves the full size.
 */
function useFitFigures(strip: React.RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const el = strip.current;
    if (!el) return undefined;
    const fit = () => {
      const current = parseFloat(el.style.getPropertyValue('--fi-stat-fit')) || 1;
      let factor = 1;
      el.querySelectorAll<HTMLElement>('[data-testid="page-header-figure"]').forEach((figure) => {
        const ink = (figure.firstElementChild?.getBoundingClientRect().width ?? 0) / current;
        const room = figure.clientWidth;
        if (ink > 0 && room > 0) factor = Math.min(factor, room / ink);
      });
      /* Floored a little under the exact fit: glyph advances round to the
         device pixel, so an exact factor can land the last digit a pixel over. */
      factor = factor < 1 ? Math.floor(factor * 0.99 * 1000) / 1000 : 1;
      if (Math.abs(factor - current) > 0.001) el.style.setProperty('--fi-stat-fit', String(factor));
    };
    fit();
    let live = true;
    /* The display face loads after first paint and is wider than its fallback. */
    document.fonts?.ready.then(() => live && fit());
    if (typeof ResizeObserver === 'undefined') return () => { live = false; };
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => {
      live = false;
      observer.disconnect();
    };
  });
}

export function PageHeaderBand({
  eyebrow,
  title,
  description,
  stats,
  actions,
  onBack,
  backLabel = 'Go back',
}: PageHeaderBandProps) {
  const statsRef = useRef<HTMLDivElement>(null);
  useFitFigures(statsRef);

  /* Out-of-range counts render as a plain grid rather than throwing; the strip
     is decoration and a page must not blank because someone passed five. */
  const columns =
    stats && stats.length >= 2 && stats.length <= 4
      ? STAT_COLUMNS[stats.length as 2 | 3 | 4]
      : 'grid-cols-2 lg:grid-cols-4';

  return (
    /* INSET, not edge to edge. The band is a panel with a margin from the
       viewport on every side, so its outer edge lines up with the content
       below it — the page's own inline padding is what sets that margin, and
       the section carries its own padding inside the rounded edge. The bottom
       border it used to need is gone with the full-bleed shape: an inset panel
       ends at its own corner, and a rule under it would be a second edge a
       pixel below the first. */
    <div className="px-[var(--fi-page-inline)] pt-[var(--fi-page-block)]">
    <section data-testid="page-header-band" className="fi-band relative isolate overflow-hidden rounded-fi-panel border border-white/10 bg-fi-brand px-5 py-6 text-white sm:px-7 sm:py-7">
      {/* Decoration, and nothing else: every layer is kept out of the
          accessibility tree and neither may receive a pointer event. They are
          painted before the content and are therefore under it, so without
          that second guard the weave would sit over the back arrow and the
          action buttons and swallow every click on them — a control that looks
          right and does nothing, which is the failure this repo keeps
          finding. */}
      <span
        data-testid="page-header-weave"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={BAND_WEAVE}
      />
      {/* The amber baseline. Its width is a constant in this file: it is a
          rule drawn under the band, not a meter, and nothing about the page's
          data may ever reach it. A plain scale width on purpose — the
          arbitrary-value spelling emitted no rule at all the last time it was
          used for a measured offset in this app. */}
      <span
        data-testid="page-header-baseline"
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-1 w-40 bg-fi-amber"
      />
      {/* `relative` only so the content wins the paint order against the two
          layers above; it establishes nothing else. */}
      <div className="relative mx-auto max-w-[1920px]">
        <div data-testid="page-header-titlerow" className="relative flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div data-testid="page-header-copy" className="fi-band__copy min-w-0 max-w-3xl">
            <p className="mb-1 text-[0.68rem] font-black uppercase tracking-[0.14em] text-white/70">{eyebrow}</p>
            {/* Plain arrow ON the title line — CLAUDE.md's rule, and it
                overrides the handoff idiom of an arrow-plus-label above it. */}
            <div data-testid="page-header-titleline" className="flex items-center gap-2">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  aria-label={backLabel}
                  className="-ml-1 shrink-0 rounded-fi-control p-1 text-white/70 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-fi-amber"
                >
                  <ArrowLeft className="size-5" aria-hidden="true" />
                </button>
              )}
              <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
            </div>
            {description && (
              <p data-testid="page-header-description" className="mt-2 text-sm leading-6 text-white/70">
                {description}
              </p>
            )}
          </div>
          {actions && (
            <div data-testid="page-header-actions" className="fi-band__actions flex flex-wrap items-center gap-2 lg:shrink-0">
              {actions}
            </div>
          )}
        </div>

        {stats && stats.length > 0 && (
          <div
            ref={statsRef}
            data-testid="page-header-stats"
            /* THE SEPARATORS ARE VERTICAL RULES BETWEEN COLUMNS, drawn as a
               left border on every child but the first, and only from `lg`,
               where every column count this component supports has collapsed
               to a single row. Below that the strip wraps, and a
               between-children border cannot tell a wrapped item from a second
               column — it would draw a rule down the left of the first stat on
               the second row, which is a line pointing at nothing. The gap does
               the separating at those widths.

               THE UNPREFIXED FORM OF THAT UTILITY IS NOT SPELLED ANYWHERE
               ABOVE, and it is written around rather than quoted: Tailwind's
               scanner reads a comment as source, so naming it here mints the
               rule with no call site. The bare one appeared in this file's
               first draft and the built-CSS diff is what found it. Describe
               the class; do not name it (CLAUDE.md).

               THE RULE IS WHITE AT 25%, AND THE NUMBER THAT SET IT IS AGAINST
               THE WEAVE, NOT AGAINST THE BAND. It shipped at 10% and was
               invisible on the rendered page in both themes — not faint,
               unreadable — and the reason is the layer it has to be told apart
               from. The weave behind it is white at 5% drawing a line every
               28px, so the question a separator has to answer here is not "can
               this be seen on navy" but "does this read as a rule rather than
               as one more thread of the texture".

               Measured in Chrome on the deployed page, as ratios against the
               band fill: the weave's own lines are 1.14, a 10% rule is 1.33 —
               1.17x the thing it is competing with, which is why four columns
               read as one field. At 25% the rule is 2.26, just under 2x the
               weave, and the columns separate at a glance without the strip
               growing edges anyone notices. The two themes land within 0.01 of
               each other because both the rule and the band are constants.

               A large ratio was never on offer and is not the target: this
               separates figures that are perfectly legible on their own, so
               what it has to beat is the texture. That is a small number by
               construction, and the mistake the first version made was
               checking it against the wrong thing.

               The previous spelling was a 1px gap over a filled parent, which
               needs every stat to carry an opaque fill of its own to mask that
               parent. That is the fill this change exists to remove, and it
               would have punched four texture-less rectangles through the
               weave besides. */
            className={`fi-band__stats mt-6 grid gap-x-5 gap-y-5 lg:gap-x-0 lg:divide-x lg:divide-white/25 ${columns}`}
          >
            {stats.map((stat) => (
              <Stat key={stat.label} {...stat} />
            ))}
          </div>
        )}
      </div>
    </section>
    </div>
  );
}

export default PageHeaderBand;
